import { useMemo, useState } from 'react';
import { ErrorState, EmptyState, LoadingState } from './RequestState';
import { PayloadViewer } from './PayloadViewer';
import { useAsyncData } from '../hooks/useAsyncData';
import { ConfirmDialog, type ConfirmDialogState } from './ConfirmDialog';
import { TrustBoundaryNotice } from './FeatureStatus';

type PrimitiveOption = { label: string; value: string };

type FieldConfig<T extends Record<string, unknown>> = {
  key: keyof T;
  label: string;
  type: 'text' | 'number' | 'textarea' | 'json' | 'checkbox' | 'select';
  options?: PrimitiveOption[];
};

type ResourcePageProps<T extends { id: string; name: string; isActive?: boolean; updatedAt: string }> = {
  title: string;
  description: string;
  fields: Array<FieldConfig<T>>;
  defaultValue: T;
  queryDefaults?: Record<string, string>;
  list: (query: Record<string, string>) => Promise<{ data: T[]; total: number }>;
  create: (payload: T) => Promise<T>;
  update: (id: string, payload: Partial<T>) => Promise<T>;
  remove: (id: string) => Promise<void>;
  rowActions?: (item: T, onChanged: () => Promise<void>) => React.ReactNode;
};

const CONFIG_PAGE_SIZE = 50;

function stringifyJson(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function createJsonDrafts<T extends Record<string, unknown>>(fields: Array<FieldConfig<T>>, value: T): Record<string, string> {
  return Object.fromEntries(
    fields
      .filter((field) => field.type === 'json')
      .map((field) => [String(field.key), stringifyJson(value[field.key])]),
  );
}

export function ConfigResourcePage<T extends { id: string; name: string; isActive?: boolean; updatedAt: string }>(
  props: ResourcePageProps<T>,
) {
  const [search, setSearch] = useState('');
  const [extraQuery, setExtraQuery] = useState<Record<string, string>>(props.queryDefaults ?? {});
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formState, setFormState] = useState<T>(props.defaultValue);
  const [jsonDrafts, setJsonDrafts] = useState<Record<string, string>>(() => createJsonDrafts(props.fields, props.defaultValue));
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);

  const requestQuery = useMemo(
    () => ({ search, ...extraQuery, page: String(page), pageSize: String(CONFIG_PAGE_SIZE) }),
    [search, extraQuery, page],
  );
  const { data, isLoading, error, refresh } = useAsyncData(
    () => props.list(requestQuery),
    [JSON.stringify(requestQuery)],
    { debounceMs: 250 },
  );
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / CONFIG_PAGE_SIZE));

  function updateField(field: keyof T, value: unknown) {
    setFormState((current) => ({ ...current, [field]: value }));
  }

  function editItem(item: T) {
    setSelectedId(item.id);
    setFormState(item);
    setJsonDrafts(createJsonDrafts(props.fields, item));
    setJsonErrors({});
    setSubmitError(null);
  }

  function resetForm() {
    setSelectedId(null);
    setFormState(props.defaultValue);
    setJsonDrafts(createJsonDrafts(props.fields, props.defaultValue));
    setJsonErrors({});
    setSubmitError(null);
  }

  function parseJsonDraft(field: FieldConfig<T>, draft = jsonDrafts[String(field.key)] ?? '{}'): unknown {
    try {
      const parsed = JSON.parse(draft.trim() || '{}');
      setJsonErrors((current) => {
        const next = { ...current };
        delete next[String(field.key)];
        return next;
      });
      updateField(field.key, parsed);
      return parsed;
    } catch {
      const message = `${field.label} must contain valid JSON.`;
      setJsonErrors((current) => ({ ...current, [String(field.key)]: message }));
      return undefined;
    }
  }

  function buildPayloadWithJson(): T | null {
    const next = { ...formState };
    const errors: Record<string, string> = {};

    for (const field of props.fields.filter((item) => item.type === 'json')) {
      const key = String(field.key);
      try {
        next[field.key] = JSON.parse((jsonDrafts[key] ?? '{}').trim() || '{}') as T[keyof T];
      } catch {
        errors[key] = `${field.label} must contain valid JSON.`;
      }
    }

    setJsonErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmitError('Fix invalid JSON before saving.');
      return null;
    }

    setFormState(next);
    return next;
  }

  async function submitForm(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);
    const payload = buildPayloadWithJson();
    if (!payload) {
      return;
    }

    try {
      setIsSubmitting(true);
      if (selectedId) {
        await props.update(selectedId, payload);
      } else {
        await props.create(payload);
      }
      resetForm();
      await refresh();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Save failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function deleteItem(id: string) {
    await props.remove(id);
    if (selectedId === id) {
      resetForm();
    }
    await refresh();
  }

  return (
    <div className="page-grid two-columns">
      <section className="panel">
        <div className="panel-header">
          <h2>{props.title}</h2>
          <p>{props.description}</p>
        </div>
        <TrustBoundaryNotice status="ADMIN_ONLY" connected detail="Advanced Admin pages write directly to persisted backend configuration records." />
        <div className="toolbar">
          <input
            placeholder="Search"
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1); }}
          />
          {'documentType' in extraQuery ? (
            <input
              placeholder="Document type"
              value={extraQuery.documentType ?? ''}
              onChange={(event) => { setExtraQuery((current) => ({ ...current, documentType: event.target.value })); setPage(1); }}
            />
          ) : null}
        </div>
        {isLoading ? <LoadingState /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && !error && data && data.data.length === 0 ? <EmptyState label="No records found." /> : null}
        {!isLoading && !error && data && data.data.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Status</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.data.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className="mono" title={item.id}>{formatRecordKey(item.id)}</td>
                  <td>{item.isActive === undefined ? 'n/a' : item.isActive ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(item.updatedAt).toLocaleString('en-GB')}</td>
                  <td className="actions-cell">
                    {props.rowActions?.(item, refresh)}
                    <button type="button" onClick={() => editItem(item)}>
                      Edit
                    </button>
                    <button type="button" className="danger-link" onClick={() => setConfirm({
                      title: 'Delete configuration record',
                      message: `Delete ${item.name}. This removes the persisted backend record and cannot be undone from this screen.`,
                      confirmLabel: 'Delete record',
                      risk: 'high',
                      requireText: 'DELETE',
                      onConfirm: () => deleteItem(item.id),
                    })}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {total > CONFIG_PAGE_SIZE ? (
          <div className="pagination-bar">
            <button type="button" className="ghost-button" disabled={page <= 1 || isLoading} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {page} of {totalPages} &mdash; {total} total</span>
            <button type="button" className="ghost-button" disabled={page >= totalPages || isLoading} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>{selectedId ? 'Edit record' : 'Create record'}</h2>
          <div className="panel-actions">
            {selectedId ? (
              <button type="button" className="ghost-button" onClick={resetForm}>
                New
              </button>
            ) : null}
          </div>
        </div>
        <form className="form-grid" onSubmit={submitForm}>
          {props.fields.map((field) => {
            const fieldValue = formState[field.key];

            if (field.type === 'checkbox') {
              return (
                <label key={String(field.key)} className="checkbox-field">
                  <input
                    type="checkbox"
                    checked={Boolean(fieldValue)}
                    onChange={(event) => updateField(field.key, event.target.checked)}
                  />
                  <span>{field.label}</span>
                </label>
              );
            }

            if (field.type === 'select') {
              return (
                <label key={String(field.key)}>
                  <span>{field.label}</span>
                  <select
                    value={String(fieldValue ?? '')}
                    onChange={(event) => updateField(field.key, event.target.value)}
                  >
                    <option value="">Select</option>
                    {(field.options ?? []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              );
            }

            if (field.type === 'textarea') {
              return (
                <label key={String(field.key)} className="full-width">
                  <span>{field.label}</span>
                  <textarea
                    rows={6}
                    value={String(fieldValue ?? '')}
                    onChange={(event) => updateField(field.key, event.target.value)}
                  />
                </label>
              );
            }

            if (field.type === 'json') {
              const key = String(field.key);
              return (
                <label key={String(field.key)} className="full-width">
                  <span>{field.label}</span>
                  <textarea
                    rows={8}
                    value={jsonDrafts[key] ?? stringifyJson(fieldValue)}
                    onChange={(event) => setJsonDrafts((current) => ({ ...current, [key]: event.target.value }))}
                    onBlur={() => parseJsonDraft(field)}
                  />
                  {jsonErrors[key] ? <span className="field-error">{jsonErrors[key]}</span> : null}
                </label>
              );
            }

            if (field.type === 'number') {
              return (
                <label key={String(field.key)}>
                  <span>{field.label}</span>
                  <input
                    type="number"
                    value={Number(fieldValue ?? 0)}
                    onChange={(event) => updateField(field.key, Number(event.target.value))}
                  />
                </label>
              );
            }

            return (
              <label key={String(field.key)}>
                <span>{field.label}</span>
                <input
                  value={String(fieldValue ?? '')}
                  onChange={(event) => updateField(field.key, event.target.value)}
                />
              </label>
            );
          })}
          {submitError ? <div className="form-error full-width">{submitError}</div> : null}
          <div className="full-width form-actions">
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : selectedId ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
        <div className="panel-subsection">
          <h3>Current form payload</h3>
          <PayloadViewer value={formState} filename="form-payload.json" />
        </div>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function formatRecordKey(id: string): string {
  if (/^[a-z0-9-]+$/i.test(id) && id.length <= 20) {
    return id;
  }

  const [prefix = 'id', rest = id] = id.split('_', 2);
  const normalized = rest.length > 0 ? `${prefix} ${rest}` : id;
  return normalized.length > 18 ? `${normalized.slice(0, 18)}...` : normalized;
}
